/**
 * Service Firebase per /operatori (PR5).
 *
 * Le mutation passano da Cloud Function (TITOLARE/ADMIN Tier 2). La lettura
 * e' direct Firestore (rules consentono read a staff Tier 1).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../security/firebase';
import { vincoloOperatoriVisibili } from './visibilita';
import type { Operatore } from '@shared/types/operatore';

export interface OperatoreRecord extends Operatore {
  id: string;
}

/**
 * PR-BO: gli operatori di backoffice sono nascosti SEMPRE, anche a un
 * utente di backoffice. Il vincolo non e' quindi condizionale: nessun
 * parametro, nessun ramo. Conseguenza accettata: se un operatore di
 * backoffice possedesse appuntamenti, nome e colore non si risolverebbero
 * in agenda (fallback grigio) — vedi docs/architecture/visibilita-backoffice.md.
 */
export async function listOperatori(opts?: {
  attiviSolo?: boolean;
}): Promise<OperatoreRecord[]> {
  const c = collection(firestore, 'operatori');
  const q = opts?.attiviSolo
    ? query(
        c,
        ...vincoloOperatoriVisibili(),
        where('stato', '==', 'ATTIVO'),
        orderBy('cognome', 'asc')
      )
    : query(c, ...vincoloOperatoriVisibili(), orderBy('cognome', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Operatore) }));
}

export async function getOperatore(id: string): Promise<OperatoreRecord | null> {
  const ref = doc(firestore, 'operatori', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Operatore) };
}

/**
 * Risolve l'operatore a partire dall'uid Auth (relazione 1-1 via campo userId).
 */
export async function getOperatoreByUserId(
  userId: string
): Promise<OperatoreRecord | null> {
  const c = collection(firestore, 'operatori');
  // PR-BO: due filtri di uguaglianza senza orderBy, serviti da merge join
  // sugli indici single-field: nessun indice composto aggiuntivo.
  const q = query(c, ...vincoloOperatoriVisibili(), where('userId', '==', userId));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...(d.data() as Operatore) };
}

// ----- mutations via Cloud Function -----

/**
 * Input per createOperatore (PR5).
 *
 * NB: `userId` non e' piu' richiesto dal client. La CF crea l'utente
 * Firebase Auth a partire dall'email e restituisce uid + operatoreId.
 * L'utente riceve via email un link di reset password (template Auth
 * nativo) e al primo accesso e' obbligato a cambiare password.
 */
export interface CreateOperatoreInput {
  email: string;
  nome: string;
  cognome: string;
  codiceFiscale: string;
  role: Operatore['role'];
  pIvaId: string;
  coloreOperatore: string;
}

export interface CreateOperatoreResult {
  operatoreId: string;
  userId: string;
}

const callCreateOperatore = httpsCallable<CreateOperatoreInput, CreateOperatoreResult>(
  functions,
  'createOperatore'
);

export async function createOperatore(
  input: CreateOperatoreInput
): Promise<CreateOperatoreResult> {
  return (await callCreateOperatore(input)).data;
}

export interface UpdateOperatoreInput {
  operatoreId: string;
  nome?: string;
  cognome?: string;
  codiceFiscale?: string;
  role?: Operatore['role'];
  pIvaId?: string;
  coloreOperatore?: string;
}

const callUpdateOperatore = httpsCallable<UpdateOperatoreInput, { ok: true }>(
  functions,
  'updateOperatore'
);

export async function updateOperatore(input: UpdateOperatoreInput) {
  return (await callUpdateOperatore(input)).data;
}

const callDeactivateOperatore = httpsCallable<{ operatoreId: string }, { ok: true }>(
  functions,
  'deactivateOperatore'
);

export async function deactivateOperatore(operatoreId: string) {
  return (await callDeactivateOperatore({ operatoreId })).data;
}
